// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    sankey,
    SankeyLink,
    SankeyNode,
} from 'd3-sankey';

// ── d3-sankey generic type parameters ────────────────────────────────────────

export interface ISankeyNodeData {
    label: string;
    color: string;
}

export interface ISankeyLinkData {
    bandColor?: string;
}

export type SankeyLayoutNode = SankeyNode<ISankeyNodeData, ISankeyLinkData>;
export type SankeyLayoutLink = SankeyLink<ISankeyNodeData, ISankeyLinkData>;

// ── Public input types ────────────────────────────────────────────────────────

export interface ISankeyNodeInput {
    id: string;
    label: string;
    color: string;
}

export interface ISankeyLinkInput {
    sourceId: string;
    targetId: string;
    value: number;
    color?: string;
}

// ── Layout constants (exported so sub-components stay in sync) ────────────────
// Coordinate space: 786 units wide. Pill tags sit above node bars (y=50–270).
// nodePadding=40 matches Figma gaps.

export const LAYOUT_WIDTH = 540;
export const NODE_BAR_WIDTH = 4;
export const NODE_PADDING = 40;
export const PILL_TOP_OFFSET_Y = 21;
export const PILL_HEIGHT = 29;
export const PILL_BORDER_RADIUS = 6;
export const LAYOUT_TOP_Y = PILL_TOP_OFFSET_Y + PILL_HEIGHT;   // 50
export const LAYOUT_BOTTOM_Y = 270;

// ── Layout helpers ────────────────────────────────────────────────────────────

/**
 * Evenly distributes nodes within each column between LAYOUT_TOP_Y and
 * LAYOUT_BOTTOM_Y. d3-sankey only guarantees the densest column fills the
 * extent; sparse columns cluster at the top without this pass.
 */
const applyEvenColumnSpacing = (nodes: SankeyLayoutNode[]): void => {
    const nodesByDepth = new Map<number, SankeyLayoutNode[]>();
    for (const node of nodes) {
        const depth = node.depth ?? 0;
        const column = nodesByDepth.get(depth) ?? [];
        column.push(node);
        nodesByDepth.set(depth, column);
    }

    for (const columnNodes of Array.from(nodesByDepth.values())) {
        columnNodes.sort((a: SankeyLayoutNode, b: SankeyLayoutNode) => (a.y0 ?? 0) - (b.y0 ?? 0));

        const nodeCount = columnNodes.length;
        const totalNodesHeight = columnNodes.reduce(
            (sum: number, node: SankeyLayoutNode) => sum + ((node.y1 ?? 0) - (node.y0 ?? 0)),
            0
        );
        const gapBetweenNodes = nodeCount > 1
            ? (LAYOUT_BOTTOM_Y - LAYOUT_TOP_Y - totalNodesHeight) / (nodeCount - 1)
            : 0;
        const singleNodeOffset = nodeCount === 1
            ? (LAYOUT_BOTTOM_Y - LAYOUT_TOP_Y - totalNodesHeight) / 2
            : 0;

        let currentY = LAYOUT_TOP_Y + singleNodeOffset;
        for (const node of columnNodes) {
            const nodeHeight = (node.y1 ?? 0) - (node.y0 ?? 0);
            node.y0 = currentY;
            node.y1 = currentY + nodeHeight;
            currentY += nodeHeight + gapBetweenNodes;
        }
    }
};

/**
 * Re-centers flow attachment points within each node bar after column spacing
 * has been adjusted. Without this pass, flows cluster at y0 instead of being
 * centered within the bar height.
 */
const recenterFlowAttachments = (nodes: SankeyLayoutNode[]): void => {
    for (const node of nodes) {
        const nodeHeight = (node.y1 ?? 0) - (node.y0 ?? 0);

        const outgoingLinks = [...(node.sourceLinks ?? [])].sort((a, b) => (a.y0 ?? 0) - (b.y0 ?? 0));
        const totalOutgoingWidth = outgoingLinks.reduce((sum, link) => sum + (link.width ?? 0), 0);
        let outgoingY = (node.y0 ?? 0) + Math.max(0, (nodeHeight - totalOutgoingWidth) / 2);
        for (const link of outgoingLinks) {
            link.y0 = outgoingY + (link.width ?? 0) / 2;
            outgoingY += link.width ?? 0;
        }

        const incomingLinks = [...(node.targetLinks ?? [])].sort((a, b) => (a.y1 ?? 0) - (b.y1 ?? 0));
        const totalIncomingWidth = incomingLinks.reduce((sum, link) => sum + (link.width ?? 0), 0);
        let incomingY = (node.y0 ?? 0) + Math.max(0, (nodeHeight - totalIncomingWidth) / 2);
        for (const link of incomingLinks) {
            link.y1 = incomingY + (link.width ?? 0) / 2;
            incomingY += link.width ?? 0;
        }
    }
};

const buildSankeyGraph = (
    nodeInputs: ISankeyNodeInput[],
    linkInputs: ISankeyLinkInput[]
): { nodes: SankeyLayoutNode[]; links: SankeyLayoutLink[] } | null => {
    if (nodeInputs.length === 0 || linkInputs.length === 0) {
        return null;
    }

    const layout = sankey<ISankeyNodeData, ISankeyLinkData>()
        .nodeId((d: SankeyLayoutNode) => (d as SankeyLayoutNode & { id: string }).id)
        .nodeWidth(NODE_BAR_WIDTH)
        .nodePadding(NODE_PADDING)
        .extent([[0, LAYOUT_TOP_Y], [LAYOUT_WIDTH, LAYOUT_BOTTOM_Y]]);

    const graph = layout({
        nodes: nodeInputs.map(n => ({ ...n })),
        links: linkInputs.map(l => ({
            source: l.sourceId,
            target: l.targetId,
            value: l.value,
            bandColor: l.color,
        })),
    }) as { nodes: SankeyLayoutNode[]; links: SankeyLayoutLink[] };

    applyEvenColumnSpacing(graph.nodes);
    recenterFlowAttachments(graph.nodes);

    return graph;
};

/**
 * Returns the x midpoint between col 1 right edge and col 2 left edge, or
 * null when the graph doesn't have both columns (caller provides the fallback).
 */
const computeDividerX = (nodes: SankeyLayoutNode[]): number | null => {
    let leftColumnRight = 0;
    let rightColumnLeft = LAYOUT_WIDTH;

    for (const node of nodes) {
        if (node.depth === 1) {
            leftColumnRight = Math.max(leftColumnRight, node.x1 ?? 0);
        } else if (node.depth === 2) {
            rightColumnLeft = Math.min(rightColumnLeft, node.x0 ?? LAYOUT_WIDTH);
        }
    }

    return leftColumnRight > 0 && rightColumnLeft < LAYOUT_WIDTH
        ? Math.round((leftColumnRight + rightColumnLeft) / 2)
        : null;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface ISankeyLayout {
    graph: { nodes: SankeyLayoutNode[]; links: SankeyLayoutLink[] } | null;
    /** null when the graph has fewer than two columns — caller supplies the fallback. */
    dividerX: number | null;
}

export const useSankeyLayout = (
    nodes: ISankeyNodeInput[],
    links: ISankeyLinkInput[]
): ISankeyLayout => {
    const graph = React.useMemo(
        () => buildSankeyGraph(nodes, links),
        [nodes, links]
    );

    const dividerX = React.useMemo(
        () => (graph ? computeDividerX(graph.nodes) : null),
        [graph]
    );

    return { graph, dividerX };
};
